import { vesselCardStyles } from "../constants/vessel-card-styles.constants";
import { VesselCardCpSource, VesselCardEvaluationPanelProps } from "../models/vessel-card.models";
import { BeakerIcon } from "@heroicons/react/24/outline";
import { twMerge } from "tailwind-merge";

const overviewItems = (performanceBasis: string | null, evaluatedAt: string | null, lastFixtureAt: string | null) => [
  {
    label: "Performance basis",
    value: performanceBasis,
    tooltip:
      "OBS_Actual uses vessel-specific data. Design, Engine and Year or Design and Year identifies a design match. No Data means no match was found.",
  },
  {
    label: "Evaluated",
    value: evaluatedAt,
    tooltip: "Date of the latest vessel performance evaluation. A new evaluation is recommended after three months.",
  },
  { label: "Last fixture date", value: lastFixtureAt, tooltip: undefined },
];

const cpSourceLabels: Record<VesselCardCpSource, string> = {
  imos: "IMOS CP Desc",
  imosInput: "IMOS Input",
  custom: "Custom CP",
};

const VesselCardEvaluationPanel = ({
  performanceBasis,
  evaluatedAt,
  lastFixtureAt,
  scrubberFitted,
  scrubberSelectionDisabled,
  scrubberSelectionHelp,
  scrubberWarning,
  onSelectScrubber,
  cpSource,
  cpSourceMessage,
  onSelectCpSource,
  onOpenCustomCp,
}: VesselCardEvaluationPanelProps) => (
  <section
    data-test="vessel-card-performance-evaluation"
    className={twMerge(vesselCardStyles.surface, "xl:col-span-9")}
  >
    <div className="mb-5 flex items-center gap-2">
      <span className="rounded-full bg-primary-20 p-2 text-green dark:bg-teal-950/60 dark:text-teal-200">
        <BeakerIcon className="h-5 w-5" />
      </span>
      <h2 className={vesselCardStyles.sectionTitle}>Performance evaluation</h2>
    </div>

    <div className="grid gap-6 md:grid-cols-[1.2fr_0.85fr_1.15fr] md:divide-x md:divide-gray-200 dark:md:divide-gray-700">
      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {overviewItems(performanceBasis, evaluatedAt, lastFixtureAt).map((item) => (
          <div key={item.label}>
            <dt className={vesselCardStyles.fieldLabel} title={item.tooltip}>
              {item.label}
            </dt>
            <dd className={twMerge(vesselCardStyles.fieldValue, "mt-1")}>{item.value ?? "—"}</dd>
          </div>
        ))}
      </dl>

      <div className="md:pl-6">
        <p className={vesselCardStyles.fieldLabel}>Scrubber selection</p>
        <div
          className="mt-2 flex gap-2"
          role="group"
          aria-label="Scrubber selection"
          aria-describedby="vessel-card-scrubber-help"
        >
          {[false, true].map((fitted) => (
            <button
              key={String(fitted)}
              type="button"
              disabled={scrubberSelectionDisabled}
              aria-pressed={scrubberFitted === fitted}
              onClick={() => onSelectScrubber(fitted)}
              data-test={`vessel-card-scrubber-${fitted ? "yes" : "no"}-button`}
              className={twMerge(
                "min-h-10 min-w-16 rounded-md border px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green disabled:cursor-not-allowed disabled:opacity-60",
                scrubberFitted === fitted
                  ? "border-green bg-green text-white"
                  : "border-gray-300 bg-white text-gray-700 hover:border-green hover:text-green dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200",
              )}
            >
              {fitted ? "Yes" : "No"}
            </button>
          ))}
        </div>
        <p
          id="vessel-card-scrubber-help"
          data-test="vessel-card-scrubber-help"
          className={twMerge(vesselCardStyles.supportingText, "mt-2")}
        >
          {scrubberSelectionHelp}
        </p>
        {scrubberWarning && (
          <p
            role="status"
            data-test="vessel-card-scrubber-warning"
            className="mt-2 rounded-md bg-amber-50 p-2 text-xs leading-5 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
          >
            {scrubberWarning}
          </p>
        )}
      </div>

      <div className="md:pl-6">
        <p className={vesselCardStyles.fieldLabel}>CP description source</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {(Object.keys(cpSourceLabels) as VesselCardCpSource[]).map((source) => (
            <button
              key={source}
              type="button"
              aria-pressed={cpSource === source}
              onClick={() => onSelectCpSource(source)}
              data-test={`vessel-card-cp-source-${source}-button`}
              className={twMerge(
                "min-h-10 rounded-md border px-3 py-2 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green",
                cpSource === source
                  ? "border-green bg-green text-white"
                  : "border-gray-300 bg-white text-gray-700 hover:border-green hover:text-green dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200",
              )}
            >
              {cpSourceLabels[source]}
            </button>
          ))}
          {cpSource === "custom" && (
            <button
              type="button"
              data-test="vessel-card-update-custom-cp-button"
              onClick={onOpenCustomCp}
              className={vesselCardStyles.secondaryButton}
            >
              Update Custom CP
            </button>
          )}
        </div>
        {cpSourceMessage && (
          <p
            role="status"
            data-test="vessel-card-cp-source-message"
            className="mt-2 text-xs leading-5 text-amber-700 dark:text-amber-300"
          >
            {cpSourceMessage}
          </p>
        )}
      </div>
    </div>
  </section>
);

export default VesselCardEvaluationPanel;
